/**
 * The one-line roster state under the cycle page header (#1909 truth pass,
 * 1 Oct 2026).
 *
 * The lead comes to the cycle page to see where the roster stands; the line
 * under the title IS that state. After a solve the next thing the lead does
 * is release the roster or tell the team, so the line says which is owed:
 *
 *   - solved, held for review     → "Solved · drafts held
 *     · Publish roster releases them to the team" (#2027: since 5 Oct the
 *     first solve of every window ends here — "hold as drafts until I
 *     publish")
 *   - solved, held by the check   → "Held · N
 *     hard-rule problems · see Roster validation" (finding 20: the old
 *     "Solved · drafts held" gave no reason)
 *   - published, a re-solve held  → "A re-solve is held for your review ·
 *     members keep the published roster · Publish roster releases it"
 *     (#2571: since #2027 step two every re-solve of dates members already
 *     have waits behind the published roster; with the check's problems it
 *     reads "Held · N hard-rule problems · see Roster validation")
 *   - published, nobody told yet  → "Published by the solve · the team has
 *     not been told yet · Publish to members" (a plain solve of a window
 *     with published rows from before #2027 left it so)
 *   - published and told          → "Published"
 *
 * Reads the facts every cycle payload carries: `roster_held_as_drafts`,
 * `roster_held_rule_problems`, `roster_resolve_held` and `members_notified_at`. A field the payload
 * does not carry (an API older than this build) reads as unknown and keeps
 * the old line — the header never claims "not told" on a guess, and a held
 * roster with no reason on it reads as held for review. Pure (no React) so
 * the wording is unit-tested on its own (`web/tests/cycleHeaderLine.test.ts`).
 */

export interface CycleHeaderFacts {
  state: string;
  /** When the team was last told; `null` = nobody has been told. */
  members_notified_at?: string | null;
  /** Not published, and a solve's roster sits as drafts. */
  roster_held_as_drafts?: boolean;
  /** #2027 — why the held roster is held: 0 = for the lead's review, N > 0 =
   *  the solve's check found N hard-rule problems. */
  roster_held_rule_problems?: number;
  /** #2571 — a re-solve of these dates waits behind the roster members have;
   *  Publish roster releases it. */
  roster_resolve_held?: boolean;
}

export type RosterReleaseState = "drafts_held" | "published_untold" | "resolve_held" | null;

/** Which release step is still owed, or `null` when the state line says it all. */
export function rosterReleaseState(cycle: CycleHeaderFacts): RosterReleaseState {
  // #2571 — a held re-solve is the step the lead owes, ahead of "told". The
  // worker holds a re-solve whenever the dates have published rows, whatever
  // the window's state reads.
  if (cycle.roster_resolve_held === true && cycle.state !== "archived") return "resolve_held";
  if (cycle.state === "published") {
    // `undefined` (field absent) is not `null`: unknown keeps the old line.
    return cycle.members_notified_at === null ? "published_untold" : null;
  }
  if (
    (cycle.state === "open" || cycle.state === "closed") &&
    cycle.roster_held_as_drafts === true
  ) {
    return "drafts_held";
  }
  return null;
}

/** The two release lines without their action — for a page that has neither
 *  a `Publish roster` nor a `Publish to members` button (the legacy dashboard). */
export const RELEASE_STATE_LINE = {
  draftsHeld: "Solved · drafts held",
  publishedUntold: "Published by the solve · the team has not been told yet",
  resolveHeld: "A re-solve is held for your review · members keep the published roster",
} as const;

export const CYCLE_HEADER_LINE = {
  draftsHeld: `${RELEASE_STATE_LINE.draftsHeld} · Publish roster releases them to the team`,
  resolveHeld: `${RELEASE_STATE_LINE.resolveHeld} · Publish roster releases it`,
  publishedUntold: `${RELEASE_STATE_LINE.publishedUntold} · Publish to members`,
  published: "Published",
  open: "Open for submissions",
  solving: "Solving…",
  closed: "Submissions closed · not solved",
  archived: "These dates have been archived. View-only.",
  setup: "Setting up.",
} as const;

/** "Held · 3 hard-rule problems · see Roster validation". */
export function heldForRuleProblemsLine(problems: number): string {
  const noun = problems === 1 ? "problem" : "problems";
  return `Held · ${problems} hard-rule ${noun} · see Roster validation`;
}

/**
 * #2027 — the held line, with its reason. Held for review it names the next
 * step (`withAction`: "· Publish roster releases them to the team"); held by
 * the check it says how many hard-rule problems and where they are listed,
 * with or without an action (Publish roster may refuse until they are fixed).
 */
export function draftsHeldLine(
  cycle: Partial<CycleHeaderFacts>,
  { withAction = false }: { withAction?: boolean } = {},
): string {
  const problems = cycle.roster_held_rule_problems ?? 0;
  if (problems > 0) return heldForRuleProblemsLine(problems);
  return withAction ? CYCLE_HEADER_LINE.draftsHeld : RELEASE_STATE_LINE.draftsHeld;
}

/**
 * #2571 — the line for a re-solve held behind the published roster. When the
 * check found hard-rule problems its line comes first (the lead must see them
 * before pressing Publish roster); otherwise it says members keep the
 * published roster.
 */
export function resolveHeldLine(
  cycle: Partial<CycleHeaderFacts>,
  { withAction = false }: { withAction?: boolean } = {},
): string {
  const problems = cycle.roster_held_rule_problems ?? 0;
  if (problems > 0) return heldForRuleProblemsLine(problems);
  return withAction ? CYCLE_HEADER_LINE.resolveHeld : RELEASE_STATE_LINE.resolveHeld;
}

/**
 * The cycle page's header line. The release lines are the lead's — they name
 * the lead's buttons and the lead's drafts — so a member keeps the state line.
 */
export function cycleHeaderLine(cycle: CycleHeaderFacts, isLead: boolean): string {
  const release = isLead ? rosterReleaseState(cycle) : null;
  if (release === "drafts_held") return draftsHeldLine(cycle, { withAction: true });
  if (release === "resolve_held") return resolveHeldLine(cycle, { withAction: true });
  if (release === "published_untold") return CYCLE_HEADER_LINE.publishedUntold;
  switch (cycle.state) {
    case "published":
      return CYCLE_HEADER_LINE.published;
    case "open":
      return CYCLE_HEADER_LINE.open;
    case "solving":
      return CYCLE_HEADER_LINE.solving;
    case "closed":
      return CYCLE_HEADER_LINE.closed;
    case "archived":
      return CYCLE_HEADER_LINE.archived;
    default:
      return CYCLE_HEADER_LINE.setup;
  }
}
